import React from 'react';
import { X } from 'lucide-react';
import { IconButton } from './IconButton';

export interface ModalProps {
  onClose: () => void;
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: string;
}

/**
 * Shared detail-modal shell (header + close + scrollable body) used by both
 * Anomaly and Recommendation detail views, so they share one visual
 * language instead of each hand-rolling its own overlay/dialog CSS.
 */
export const Modal: React.FC<ModalProps> = ({ onClose, title, subtitle, headerActions, children, maxWidth = 'max-w-3xl' }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`flex max-h-[90vh] w-full ${maxWidth} flex-col rounded-lg bg-card shadow-xl dark:bg-card`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sized to sit just above the body's own section headings
            rather than at page-title scale - a dialog opened over a page
            shouldn't out-shout the page behind it. */}
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-3.5 dark:border-border">
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
            {subtitle && <p className="mt-0.5 truncate text-[11px] font-mono text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerActions}
            <IconButton onClick={onClose} aria-label="Close">
              <X />
            </IconButton>
          </div>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
};

export interface PillTabsProps {
  tabs: Array<{ key: string; label: string; icon?: React.ElementType }>;
  active: string;
  onChange: (key: string) => void;
}

/**
 * Segmented tab strip for detail views.
 *
 * Metrics match the reference product's tab bar exactly - a `bg-card` strip
 * with a `p-1` gutter, `px-3 py-2 text-xs` triggers and a solid `bg-primary`
 * active state. Shared by the anomaly and recommendation detail views so the
 * two can't drift: two detail screens with subtly different tab metrics is
 * precisely what makes an app feel assembled rather than designed.
 */
export const PillTabs: React.FC<PillTabsProps> = ({ tabs, active, onChange }) => (
  <div className="mb-4 flex gap-1 overflow-x-auto rounded-lg border bg-card p-1 shadow-sm">
    {tabs.map((tab) => {
      const Icon = tab.icon;
      const isActive = tab.key === active;
      return (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors ${
            isActive
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-secondary hover:text-secondary-foreground'
          }`}
        >
          {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
          {tab.label}
        </button>
      );
    })}
  </div>
);
